import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getOwnerOnboarding, saveOwnerOnboarding } from "../../api/onboarding";
import { GUIDE_VERSION, guideSteps, parseGuideResponse } from "./content";
import { OwnerGuideDialog } from "./dialog";
import "./style.css";
import { OwnerGuideContext as GuideContext } from "./context";
export const OwnerOnboardingProvider = ({ children }) => {
  const logged = useSelector((s) => s.aplicationConfig.userConfig.logged),
    user = useSelector((s) => s.aplicationConfig.userConfig.userData),
    navigate = useNavigate();
  const key =
    logged && user?.role === "admin" && user?.userId
      ? `${window.location.host}:${user.userId}:${user.corporationId || ""}`
      : null;
  const keyRef = useRef(key);
  keyRef.current = key;
  const lock = useRef(false),
    readEpoch = useRef(0);
  const [state, setState] = useState({
      key: null,
      record: null,
      error: "",
      loading: false,
    }),
    [openedFor, setOpenedFor] = useState(null),
    [step, setStep] = useState(0),
    [busy, setBusy] = useState(false),
    [writeError, setWriteError] = useState("");
  const current = state.key === key ? state : null,
    record = current?.record;
  const open = !!key && openedFor === key && record?.eligible === true;
  useEffect(() => {
    let active = true;
    const epoch = ++readEpoch.current;
    lock.current = false;
    setBusy(false);
    setOpenedFor(null);
    setWriteError("");
    setState({ key, record: null, error: "", loading: !!key });
    if (!key) return;
    getOwnerOnboarding()
      .then((response) => {
        if (!active || keyRef.current !== key || epoch !== readEpoch.current) return;
        const next = parseGuideResponse(response);
        setState({ key, record: next, error: "", loading: false });
        if (next.eligible && ["new", "in_progress"].includes(next.status)) {
          setStep(next.step);
          setOpenedFor(key);
        }
      })
      .catch(() => {
        if (active && keyRef.current === key && epoch === readEpoch.current)
          setState({
            key,
            record: null,
            error:
              "No se pudo cargar la guía de inicio. Puedes seguir usando el Admin.",
            loading: false,
          });
      });
    return () => {
      active = false;
    };
  }, [key]);
  useEffect(() => {
    const warn = (e) => {
      if (busy) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);
  const openGuide = async () => {
    if (!key || lock.current) return;
    const epoch = ++readEpoch.current;
    lock.current = true;
    setState((s) => ({ ...s, error: "", loading: true }));
    try {
      const next = parseGuideResponse(await getOwnerOnboarding());
      if (keyRef.current !== key || epoch !== readEpoch.current) return;
      setState({ key, record: next, error: "", loading: false });
      if (next.eligible) {
        setStep(next.status === "completed" ? 0 : next.step);
        setWriteError("");
        setOpenedFor(key);
      }
    } catch {
      if (keyRef.current === key)
        setState((s) => ({
          ...s,
          loading: false,
          error: "No se pudo cargar la guía de inicio. Vuelve a intentarlo.",
        }));
    } finally {
      if (keyRef.current === key) lock.current = false;
    }
  };
  const persist = async (status, nextStep, after) => {
    if (!key || !record?.eligible || lock.current) return;
    lock.current = true;
    setBusy(true);
    setWriteError("");
    try {
      const next =
        record.status === "completed"
          ? record
          : parseGuideResponse(
              await saveOwnerOnboarding({
                version: GUIDE_VERSION,
                status,
                step: nextStep,
              }),
            );
      if (keyRef.current !== key) return;
      if (
        !next.eligible ||
        (status === "completed" && next.status !== "completed") ||
        (next.status !== "completed" &&
          (next.status !== status || next.step < nextStep))
      )
        throw Error();
      setState({ key, record: next, error: "", loading: false });
      after(next);
    } catch {
      if (keyRef.current === key)
        setWriteError(
          "No se pudo guardar el progreso. Vuelve a intentarlo; el Admin sigue disponible.",
        );
    } finally {
      if (keyRef.current === key) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const pause = (path) =>
    persist("paused", step, () => {
      setOpenedFor(null);
      if (path) navigate(path);
    });
  const next = () =>
    step === guideSteps.length - 1
      ? persist("completed", step, () => setOpenedFor(null))
      : persist("in_progress", step + 1, () => setStep(step + 1));
  const goStep = (value) => {
    if (lock.current) return;
    setStep(value);
    setWriteError("");
  };
  return (
    <GuideContext.Provider
      value={{
        record,
        error: current?.error,
        loading: current?.loading,
        openGuide,
      }}
    >
      <div className="ownerGuideApp" inert={open ? "" : undefined}>
        {children}
      </div>
      {open && (
        <OwnerGuideDialog
          step={step}
          furthest={
            record.status === "completed"
              ? guideSteps.length - 1
              : Math.max(record.step, step)
          }
          busy={busy}
          error={writeError}
          onStep={goStep}
          onNext={next}
          onPause={() => pause()}
          onSection={pause}
          onExitWithoutSave={() => {
            if (!lock.current) setOpenedFor(null);
          }}
        />
      )}
    </GuideContext.Provider>
  );
};
export { OwnerGuideEntry } from "./entry";
